import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { formatDate, API_URL } from "../../lib/utils";
import { KeyRound, Copy, Plus, XCircle } from "lucide-react";

const STATUS = {
  active: { label: "Ativo", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  used: { label: "Usado", cls: "bg-zinc-500/15 text-zinc-500 border-zinc-500/30" },
  expired: { label: "Expirado", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  revoked: { label: "Revogado", cls: "bg-red-500/15 text-red-600 border-red-500/30" },
};

export const CancelCodesDialog = ({ open, onClose, headers }) => {
  const [codes, setCodes] = useState([]);
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/invoices/cancel-codes`, { headers, withCredentials: true });
      setCodes(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar códigos");
    }
  };

  useEffect(() => {
    if (open) { setFresh(null); load(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/invoices/cancel-codes`, {}, { headers, withCredentials: true });
      setFresh(res.data);
      toast.success("Código de anulação gerado — válido 24h, uso único");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar código");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (c) => {
    try {
      await axios.delete(`${API_URL}/invoices/cancel-codes/${c.code_id}`, { headers, withCredentials: true });
      toast.success("Código revogado");
      if (fresh?.code_id === c.code_id) setFresh(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao revogar");
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code).then(() => toast.success("Código copiado")).catch(() => {});
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cancel-codes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> Códigos de Anulação de Faturas</DialogTitle>
          <DialogDescription>
            Só o administrador gera códigos. Cada código autoriza <strong>uma</strong> anulação, expira em 24h e fica sempre registado quem gerou e quem utilizou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 min-w-0 overflow-hidden">
          <Button onClick={generate} disabled={busy} className="w-full gap-2" data-testid="generate-cancel-code-btn">
            <Plus className="w-4 h-4" /> Gerar novo código
          </Button>

          {fresh && (
            <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4 text-center space-y-2" data-testid="fresh-cancel-code">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Novo código (entregue ao operador)</p>
              <p className="font-mono text-4xl font-black tracking-[0.35em]" data-testid="fresh-cancel-code-value">{fresh.code}</p>
              <p className="text-xs text-muted-foreground">Válido até {formatDate(fresh.expires_at)}</p>
              <Button size="sm" variant="outline" onClick={() => copy(fresh.code)} className="gap-1.5" data-testid="copy-cancel-code-btn">
                <Copy className="w-3.5 h-3.5" /> Copiar
              </Button>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto divide-y rounded-lg border" data-testid="cancel-codes-list">
            {codes.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">Ainda não foram gerados códigos</p>}
            {codes.map((c) => {
              const st = STATUS[c.status] || STATUS.active;
              return (
                <div key={c.code_id} className="flex items-center gap-3 p-3 text-sm min-w-0" data-testid={`cancel-code-row-${c.code_id}`}>
                  <span className="font-mono font-bold tracking-widest shrink-0">{c.status === "active" ? c.code : `••${c.code.slice(-2)}`}</span>
                  <Badge variant="outline" className={`${st.cls} shrink-0`}>{st.label}</Badge>
                  <div className="flex-1 min-w-0 text-xs text-muted-foreground truncate">
                    {c.source === "platform" ? "LUMORA" : c.generated_by_name} · {formatDate(c.created_at)}
                    {c.status === "used" && ` → ${c.invoice_number} por ${c.used_by_name} (${formatDate(c.used_at)})`}
                  </div>
                  {c.status === "active" && (
                    <button onClick={() => revoke(c)} className="text-red-500 hover:text-red-600" title="Revogar" data-testid={`revoke-cancel-code-${c.code_id}`}>
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
